import { noop } from '@capsicum/common';
import { usePoblanoTutorialEligibility } from '@capsicum/features/feeds/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { type ReactNode } from 'react';
import styled from 'styled-components';

import { useV2MigrationStatus } from '../../hooks/useV2MigrationStatus';
import { useWeb3Migration } from '../../hooks/useWeb3Migration';
import { NCWalletActionBar } from '../../ui/nc-wallet-action-bar/NCWalletActionBar';
import { NCWalletBalance } from '../../ui/nc-wallet-balance/NCWalletBalance';
import { NCWalletMarketingBanner } from '../../ui/nc-wallet-marketing-banner/NCWalletMarketingBanner';
import { NCWFallbackBalance } from '../../ui/ncw-fallback-balance';
import { NCWMigrationIndicator } from '../../ui/ncw-migration-indicator/NCWMigrationIndicator';

export const NCWalletHeaderContent = () => {
	const { isExistingUser } = usePoblanoTutorialEligibility();
	const { isLoading: isV2MigrationStatusLoading, isMigrating: isV2MigrationLoading } = useV2MigrationStatus();
	const { isMigrationDataLoading, isMigrationDone, isMigrationError, isMigrationFailed, isMigrationOngoing } =
		useWeb3Migration();
	const { isPending, ncwBalance } = useNCWBalance();

	let balance: ReactNode = <NCWalletBalance isLoading />;

	if (isMigrationDone && !isV2MigrationLoading) {
		if (ncwBalance?.totalFiatBalance) {
			// Show the balance
			balance = <NCWalletBalance />;
		} else if (!isPending) {
			// "Please reload your wallet to see your balance", shown when we failed to load the balance
			balance = <NCWFallbackBalance />;
		}
	} else if (isMigrationError || isMigrationDataLoading || isMigrationFailed || isV2MigrationStatusLoading) {
		// Show NCWalletBalance loading skeleton when /web3-migrate errors out or is still loading
		noop();
	} else if ((isExistingUser && isMigrationOngoing) || isV2MigrationLoading) {
		// Only users with an old custodial wallet should see the migration message "Your assets are still being transferred"
		balance = <NCWMigrationIndicator variant={isExistingUser && isMigrationOngoing ? 'ncw' : 'charapita'} />;
	}

	return (
		<div>
			<S.TutorialWrapper data-tutorial="walletLobbyEntry">
				{balance}
				<NCWalletActionBar />
			</S.TutorialWrapper>

			<NCWalletMarketingBanner />
		</div>
	);
};

namespace S {
	export const TutorialWrapper = styled.div`
		margin: 0 ${({ theme }) => theme.spacings.xsmall};
	`;
}
